import React, { useState, useMemo } from 'react';
import {
  Box, Container, Flex, HStack, VStack, Heading, Text, Icon, Button, Progress,
  Checkbox, Badge, Divider, Input, FormControl, FormLabel, Circle,
  UnorderedList, ListItem, SimpleGrid,
} from '@chakra-ui/react';
import {
  FaRoute, FaCarSide, FaMapMarkerAlt, FaVial, FaCheckCircle, FaClipboardCheck,
  FaCamera, FaTruck, FaClock, FaCog, FaHandshake,
} from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

// A self-contained, shareable onboarding walkthrough. Public link — no login
// required — so it doubles as a training resource you can send to new phlebs.
// Each step must be acknowledged ("Got it") before advancing; the final screen
// captures the phleb's name and logs the completion to phleb_training_ack.

const STEPS = [
  {
    key: 'welcome', icon: FaHandshake, color: 'blue',
    title: 'Welcome to At-Home Blood Draw',
    lead: "This quick walkthrough shows how a draw flows from your list to the lab. It takes ~3 minutes. Tick “Got it” on each step to continue.",
    points: [
      'You’ll pick up draws, visit patients, collect and spin specimens, then drop them for the lab.',
      'Everything happens in the phleb portal on your phone — no paperwork.',
      'At the end you’ll confirm you understand the process.',
    ],
    ack: 'I’m ready to start the walkthrough',
  },
  {
    key: 'tabs', icon: FaRoute, color: 'purple',
    title: 'Your Draws — the four tabs',
    lead: 'Your home screen lists patients as cards, split into tabs:',
    points: [
      'My Route — draws you’ve confirmed you’ll do. This is your work for the day.',
      'Available — draws in your area you can take, plus any an admin assigned to you that you haven’t confirmed yet.',
      'Ready to drop — specimens you’ve spun and photographed, waiting to be dropped at the lab.',
      'Completed — draws you’ve finished and dropped.',
    ],
    ack: 'I understand the four tabs',
  },
  {
    key: 'claim', icon: FaCheckCircle, color: 'green',
    title: 'Add a draw to your route',
    lead: 'A draw is only yours once you confirm it.',
    points: [
      'On the Available tab, tap “Add to route” to claim a draw — it moves to My Route.',
      'If an admin assigned you a draw, it still shows under Available until you add it to your route.',
      'On Available, you’ll only see “Add to route” and “Open” — claim it first, then you can update its status.',
    ],
    ack: 'I know how to add a draw to my route',
  },
  {
    key: 'enroute', icon: FaCarSide, color: 'orange',
    title: 'Heading out — “En route”',
    lead: 'When you leave for a patient, open the draw and tap En route. You’ll be asked: did you contact the patient?',
    points: [
      'Yes — you’ll leave a short note on what the patient said (e.g. “confirmed, buzzer #4”). This is logged for the admin.',
      'No — you’ll be prompted to call the patient first. Always make contact before driving out.',
      'Use the Call / Text buttons on the card to reach them.',
    ],
    ack: 'I’ll contact the patient before heading out',
  },
  {
    key: 'arrive', icon: FaMapMarkerAlt, color: 'teal',
    title: 'Arrived',
    lead: 'Once you’re at the door, tap Arrived. Use Directions / Map any time to navigate.',
    points: [
      'Confirm the patient’s identity: full name and date of birth.',
      'Then move on to collecting the specimen.',
    ],
    ack: 'I’ll verify patient name + DOB on arrival',
  },
  {
    key: 'tubeguide', icon: FaClipboardCheck, color: 'cyan',
    title: 'Tube guide — check off every tube',
    lead: 'Before you can mark the draw done, you must check off each tube in the test tube guide.',
    points: [
      'The guide lists exactly which tubes/colors to draw and in what order.',
      'Tick each one as you fill it. The “Drawn” step stays locked until all are checked.',
      'This makes sure nothing is missed for the lab’s tests.',
    ],
    ack: 'I’ll complete the tube guide before marking Drawn',
  },
  {
    key: 'outcome', icon: FaVial, color: 'red',
    title: 'Mark the draw',
    lead: 'Record the result honestly — first pick Successful or Unsuccessful.',
    points: [
      'Successful draw — sample collected. You can also flag: difficult stick, partial draw, multiple attempts, hematoma.',
      'Unsuccessful — couldn’t collect; add a note on why so the admin can follow up.',
      'Add any note that helps (patient condition, access issues, etc.).',
    ],
    ack: 'I know how to record the draw result + flags',
  },
  {
    key: 'spin', icon: FaCamera, color: 'pink',
    title: 'Spin & label the specimen',
    lead: 'After a successful draw, spin the blood, then label and photograph it.',
    points: [
      'Label the tube with exactly what the app shows: Order ID, patient name, and DOB.',
      'Take the required photo of the labeled specimen — you can’t mark it ready without it.',
      'Tap “Mark ready for drop” — it moves to your Ready to drop tab.',
    ],
    ack: 'I’ll label with Order ID + name + DOB and photograph it',
  },
  {
    key: 'drop', icon: FaTruck, color: 'blue',
    title: 'Drop the batch',
    lead: 'Your spun specimens wait on the Ready to drop tab until you hand them off.',
    points: [
      'Each specimen shows a green “Drop at:” destination — this is set by the admin and tells you exactly where it goes.',
      'It’s either a drop-off location you drive to, or a courier pickup from your address. You can’t change it — just follow it.',
      'Open any specimen to add/fix its photo. When they’re all handed off, tap “Drop batch” — they move to Completed.',
    ],
    ack: 'I’ll drop each specimen where the app tells me',
  },
  {
    key: 'gps', icon: FaClock, color: 'gray',
    title: 'Location & hours',
    lead: 'While the portal is open, it logs your location every couple of minutes.',
    points: [
      'This is how your working hours and route are tracked — please allow location access.',
      'It only runs while the portal is open on your phone.',
    ],
    ack: 'I’ll keep location access on while working',
  },
  {
    key: 'settings', icon: FaCog, color: 'purple',
    title: 'Keep your profile current',
    lead: 'In Settings, keep these up to date:',
    points: [
      'Service ZIP codes — which areas you cover (drives what Available shows you).',
      'Working hours and your home/pickup address.',
      'Accurate info means the right draws reach you and courier pickups go to the right place.',
    ],
    ack: 'I’ll keep my service area and address current',
  },
];

function StepIcon({ icon, color, active, done }) {
  return (
    <Circle size="44px" bg={done ? `${color}.500` : active ? `${color}.100` : 'gray.100'}
      color={done ? 'white' : active ? `${color}.600` : 'gray.400'} transition="all 0.2s">
      <Icon as={done ? FaCheckCircle : icon} boxSize={5} />
    </Circle>
  );
}

function PhlebTraining() {
  const [idx, setIdx] = useState(0);
  const [acked, setAcked] = useState({});          // step key -> bool
  const [finalChecks, setFinalChecks] = useState({ workflow: false, honest: false, contact: false });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const total = STEPS.length;
  const onFinalScreen = idx === total;      // one past the last step
  const step = STEPS[idx];
  const pct = Math.round((Math.min(idx, total) / total) * 100);

  const allFinal = finalChecks.workflow && finalChecks.honest && finalChecks.contact && name.trim().length > 1;

  const next = () => setIdx((i) => Math.min(i + 1, total));
  const back = () => setIdx((i) => Math.max(i - 1, 0));

  const submit = async () => {
    if (!allFinal) return;
    setSubmitting(true);
    try {
      // Best-effort log — the page still completes even if the table isn't there yet.
      await supabase.from('phleb_training_ack').insert({
        full_name: name.trim(),
        email: email.trim() || null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
    } catch (e) {
      // ignore — training completion shouldn't hard-fail on logging
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  };

  const progressColor = useMemo(() => (onFinalScreen ? 'green' : (step?.color || 'blue')), [onFinalScreen, step]);

  if (done) {
    return (
      <Box minH="100vh" bg="gray.50" py={12}>
        <Container maxW="lg">
          <VStack spacing={5} bg="white" p={10} borderRadius="2xl" boxShadow="md" textAlign="center">
            <Circle size="72px" bg="green.500" color="white"><Icon as={FaCheckCircle} boxSize={9} /></Circle>
            <Heading size="lg">You’re all set, {name.trim().split(' ')[0]}!</Heading>
            <Text color="gray.600">
              Thanks for completing the walkthrough. You understand how draws flow from your list to the lab.
              Log in to the phleb portal to see your draws.
            </Text>
            <Button as="a" href="/phlebotomist/login" colorScheme="blue" size="lg">Go to the portal</Button>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 6, md: 10 }}>
      <Container maxW="2xl">
        {/* Header + progress */}
        <VStack spacing={2} mb={5}>
          <Heading size="md" color="gray.700">Phlebotomist Onboarding</Heading>
          <Text fontSize="sm" color="gray.500">{onFinalScreen ? 'Final confirmation' : `Step ${idx + 1} of ${total}`}</Text>
          <Progress value={pct} colorScheme={progressColor} size="sm" w="full" borderRadius="full" />
        </VStack>

        <Box bg="white" borderRadius="2xl" boxShadow="md" p={{ base: 5, md: 8 }}>
          {!onFinalScreen ? (
            <VStack align="stretch" spacing={5}>
              <HStack spacing={4} align="center">
                <StepIcon icon={step.icon} color={step.color} active done={false} />
                <Box>
                  <Badge colorScheme={step.color} mb={1}>{step.key.toUpperCase()}</Badge>
                  <Heading size="lg" color="gray.800">{step.title}</Heading>
                </Box>
              </HStack>

              <Text fontSize="md" color="gray.700">{step.lead}</Text>

              <UnorderedList spacing={2} pl={2} color="gray.700">
                {step.points.map((p, i) => (
                  <ListItem key={i} fontSize="sm">{p}</ListItem>
                ))}
              </UnorderedList>

              <Divider />

              <Checkbox
                colorScheme={step.color}
                size="lg"
                isChecked={!!acked[step.key]}
                onChange={(e) => setAcked((prev) => ({ ...prev, [step.key]: e.target.checked }))}
              >
                <Text fontWeight="600" fontSize="sm">{step.ack}</Text>
              </Checkbox>

              <Flex justify="space-between" pt={1}>
                <Button variant="ghost" onClick={back} isDisabled={idx === 0}>Back</Button>
                <Button colorScheme={step.color} onClick={next} isDisabled={!acked[step.key]}>
                  {idx === total - 1 ? 'Finish' : 'Next'}
                </Button>
              </Flex>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={5}>
              <HStack spacing={4} align="center">
                <Circle size="44px" bg="green.100" color="green.600"><Icon as={FaClipboardCheck} boxSize={5} /></Circle>
                <Heading size="lg" color="gray.800">Confirm you understand</Heading>
              </HStack>

              <Text fontSize="sm" color="gray.600">Please confirm the essentials before you start taking draws:</Text>

              <VStack align="stretch" spacing={3} bg="gray.50" p={4} borderRadius="lg">
                <Checkbox colorScheme="green" isChecked={finalChecks.workflow}
                  onChange={(e) => setFinalChecks((p) => ({ ...p, workflow: e.target.checked }))}>
                  <Text fontSize="sm">I understand the full workflow: claim → contact → arrive → tube guide → draw → spin + photo → drop.</Text>
                </Checkbox>
                <Checkbox colorScheme="green" isChecked={finalChecks.contact}
                  onChange={(e) => setFinalChecks((p) => ({ ...p, contact: e.target.checked }))}>
                  <Text fontSize="sm">I’ll contact the patient before heading out and drop each specimen where the app directs.</Text>
                </Checkbox>
                <Checkbox colorScheme="green" isChecked={finalChecks.honest}
                  onChange={(e) => setFinalChecks((p) => ({ ...p, honest: e.target.checked }))}>
                  <Text fontSize="sm">I’ll label specimens with Order ID + name + DOB, photograph them, and record outcomes honestly.</Text>
                </Checkbox>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Your full name</FormLabel>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="First Last" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Email (optional)</FormLabel>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
                </FormControl>
              </SimpleGrid>

              <Flex justify="space-between" pt={1}>
                <Button variant="ghost" onClick={back}>Back</Button>
                <Button colorScheme="green" onClick={submit} isLoading={submitting} isDisabled={!allFinal}
                  leftIcon={<Icon as={FaCheckCircle} />}>
                  I understand — complete onboarding
                </Button>
              </Flex>
            </VStack>
          )}
        </Box>

        {/* Step dots */}
        {!onFinalScreen && (
          <HStack spacing={1.5} justify="center" mt={5} flexWrap="wrap">
            {STEPS.map((s, i) => (
              <Circle key={s.key} size="9px"
                bg={i < idx ? `${s.color}.400` : i === idx ? `${s.color}.600` : 'gray.300'} />
            ))}
          </HStack>
        )}
      </Container>
    </Box>
  );
}

export default PhlebTraining;
